import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { FormCoverImageFile, FormSetupData } from '@features/forms/types';
import generateSHA256Hash from '@utils/crypto';
import { getDocumentDisplayUrl } from '@utils/downloadFile';

import { COVER_IMAGE_MAX_SIZE, FORM_PRESET_COVER_IMAGES } from '../constants';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

export interface CoverImageFileCredentials {
  fileId: string;
  accessKey: string;
  signature?: string;
}

type DownloadFormFileResult = {
  data?: unknown;
  error?: unknown;
};

const normalizeImageUrl = (imageUrl: string) =>
  imageUrl.replace(/\\u0026/gi, '&');

const parseSignatureFromImageUrl = (imageUrl: string): string | undefined => {
  const normalizedUrl = normalizeImageUrl(imageUrl);

  try {
    return new URL(normalizedUrl).searchParams.get('signature') ?? undefined;
  } catch {
    const match = normalizedUrl.match(/[?&]signature=([^&]+)/);
    return match?.[1];
  }
};

export const parseCoverImageFileCredentials = (
  imageUrl: string
): CoverImageFileCredentials | null => {
  const normalizedUrl = normalizeImageUrl(imageUrl);
  const signature = parseSignatureFromImageUrl(normalizedUrl);

  const [fileId, accessKey] = normalizedUrl.split('|');
  if (fileId && accessKey && !normalizedUrl.startsWith('http')) {
    return { fileId, accessKey, signature };
  }

  const downloadPathMatch = normalizedUrl.match(
    /\/_files\/download\/([^/?]+)\/([^/?]+)/
  );
  if (downloadPathMatch?.[1] && downloadPathMatch?.[2]) {
    return {
      fileId: downloadPathMatch[1],
      accessKey: downloadPathMatch[2],
      signature
    };
  }

  return null;
};

export const resolveCoverImageCredentials = (
  setup: Pick<FormSetupData, 'coverImageFile' | 'coverImageUrl'>
): FormCoverImageFile | null => {
  if (setup.coverImageFile?.fileId && setup.coverImageFile?.accessKey) {
    return setup.coverImageFile;
  }

  if (!setup.coverImageUrl) {
    return null;
  }

  const parsed = parseCoverImageFileCredentials(setup.coverImageUrl);
  if (!parsed) {
    return null;
  }

  return {
    fileId: parsed.fileId,
    accessKey: parsed.accessKey,
    signature: parsed.signature
  };
};

export const extractPresignedUrlFromDownloadResponse = (
  response: unknown
): string | null => {
  const payload =
    response && typeof response === 'object' && 'data' in response
      ? (response as { data: unknown }).data
      : response;

  if (typeof payload === 'string') {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, string>;
  return record.url || record.presignedUrl || record.presigned_url || null;
};

export const loadCoverImagePreviewFromDownload = async (
  credentials: CoverImageFileCredentials,
  downloadFormFile: (args: {
    fileId: string;
    accessKey: string;
    signature: string;
  }) => Promise<DownloadFormFileResult>
): Promise<string> => {
  const signature =
    credentials.signature ??
    (await generateSHA256Hash(
      `${credentials.fileId}|${credentials.accessKey}`
    ));

  const result = await downloadFormFile({
    fileId: credentials.fileId,
    accessKey: credentials.accessKey,
    signature
  });

  if (result.error) {
    throw new Error('Failed to download cover image');
  }

  const presignedUrl = extractPresignedUrlFromDownloadResponse(result.data);
  if (!presignedUrl) {
    throw new Error('Failed to resolve cover image download URL');
  }

  return getDocumentDisplayUrl(presignedUrl);
};

export const buildCoverImageFileName = (file: File, timestamp = Date.now()) => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const baseName = file.name.includes('.')
    ? file.name.slice(0, file.name.lastIndexOf('.'))
    : file.name;

  return extension
    ? `${baseName}_${timestamp}.${extension}`
    : `${baseName}_${timestamp}`;
};

const getFileNameFromUrl = (imageUrl: string) => {
  const fileName = imageUrl.split('/').pop()?.split('?')[0];
  return fileName || 'cover_image.png';
};

export const resolveCoverImageUploadUrl = (imageUrl: string) => {
  const preset = FORM_PRESET_COVER_IMAGES.find(
    (image) => image.url === imageUrl
  );

  return preset?.uploadPath ?? imageUrl;
};

export const fetchCoverImageFileFromUrl = async (
  imageUrl: string
): Promise<File | null> => {
  try {
    const uploadUrl = resolveCoverImageUploadUrl(imageUrl);
    const response = await fetch(uploadUrl, { credentials: 'include' });
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    const fileName = getFileNameFromUrl(uploadUrl);
    const mimeType = blob.type || 'image/png';

    return new File([blob], fileName, { type: mimeType });
  } catch {
    return null;
  }
};

export const loadCoverImageCropPreview = async (
  imageUrl: string
): Promise<{ previewUrl: string; fileName: string } | null> => {
  const file = await fetchCoverImageFileFromUrl(imageUrl);
  if (!file) {
    return null;
  }

  return {
    previewUrl: URL.createObjectURL(file),
    fileName: file.name
  };
};

const isRemoteImageUrl = (imageUrl: string) =>
  imageUrl.startsWith('http://') || imageUrl.startsWith('https://');

export const fetchImagePreviewUrl = async (
  imageUrl: string
): Promise<string> => {
  if (!isRemoteImageUrl(imageUrl)) {
    return imageUrl;
  }

  const fetchUrl = resolveCoverImageUploadUrl(imageUrl);
  const response = await fetch(fetchUrl, { credentials: 'include' });

  if (!response.ok) {
    throw new Error('Failed to load image preview');
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const readCoverImageAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result === 'string') {
        resolve(result);
        return;
      }
      reject(new Error('Failed to read image'));
    };
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });

export const validateCoverImageFile = (selectedFile: File): string | null => {
  const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

  if (fileExtension === 'jpeg') {
    return 'Upload failed. Unsupported file type';
  }

  if (selectedFile.size > COVER_IMAGE_MAX_SIZE) {
    sendMixpanelEvent(Event.IMAGE_UPLOAD_ERROR, {
      validation_error: 'Image size exceeded 2 MB',
      image_type: 'form_cover'
    });
    return 'Please select a file smaller than 2 MB.';
  }

  if (selectedFile.type && !ACCEPTED_IMAGE_TYPES.includes(selectedFile.type)) {
    return 'Upload failed. Unsupported file type';
  }

  return null;
};
